import { createElement } from 'react';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

import ODDS_FORMAT from '~/enums/odds_format';
import US_STATE_CODE from '~/enums/us-state-code';
import { type StateCode } from '~/services/actions/state-actions';

import type IConfigData from '../../../types/config-data';

import { type MyOCData } from '~/types/myoc';
import type INavigationLink from './interfaces/navigation-link';
import { REPUBS } from '@cx-modules/constants';

interface InitialState {
	config: IConfigData & {
		freeBetsCount?: number;
		intBetslipCookie?: any;
		labels: Partial<Record<string, any>>;
		showFooter?: false;
	};
	cookies: Record<string, any>;
	myoc: Partial<MyOCData>;
	nav: {
		bottomNav: INavigationLink[];
	};
	nextUpEvents?: {
		tabs: {
			events: any[];
		}[];
	};
	nextUpEventsRacing?: any[];
	page: any;
}

const initialStateData: InitialState = {
	myoc: {
		isLoggedIn: false,
	},
	config: {
		baseUrl: '',
		categoryGroupName: 'football',
		contentServer: '',
		currentPath: '/',
		diffusionServer: '',
		environment: '',
		freeBetsCount: 0,
		geoIpLocation: 'US',
		geoStateLocation: 'NJ',
		geoStateLocationOrigin: 'NJ',
		isFontLoaded: '',
		isIE: false,
		labels: {
			RESPONSIVE_MORE: 'More',
			RESPONSIVE_ODDS_FORMAT: 'Odds Format',
			RESPONSIVE_SIGN_IN: 'Sign in',
			RESPONSIVE_BETSLIP: 'Betslip',
		},
		lang: 'en',
		repub: REPUBS.US.name,
		staticUrl: 'https://cx-resources.oddschecker.com/public/',
	},
	nav: {
		bottomNav: [
			{
				text: 'Home',
				icon: 'Home',
				url: '',
				isSelected: false,
			},
			{
				text: 'AZ Sports',
				icon: 'sports',
				url: '',
				isSelected: false,
			},
			{
				text: 'Picks',
				icon: 'Picks',
				url: 'picks-parlays',
				isSelected: false,
			},
			{
				text: 'Betslip',
				icon: 'betslip',
				url: '',
				isSelected: false,
			},
		],
	},
	page: {
		id: 711992,
		name: 'Homepage New',
		metaTitle:
			'OddsChecker | Betting Odds, Daily Tips, Bookie Offers, Free Bets,',
		metaDescription:
			"OddsChecker | Betting Odds, Daily Tips, Bookie Offers, Free Bets, you're a hero",
		pageView: 'HOMEPAGE_NEW',
		isRacing: false,
		template: 'StackedColumn',
		layout: [],
		relatedPages: [
			{
				repub: REPUBS.OC.name,
				url: 'homepage',
				hreflang: 'en-gb',
			},
			{
				repub: REPUBS.ES.name,
				url: 'homepage',
				hreflang: 'es-es',
			},
			{
				repub: REPUBS.IT.name,
				url: 'homepage',
				hreflang: 'it-it',
			},
			{
				repub: REPUBS.US.name,
				url: 'homepage',
				hreflang: 'en-us',
			},
		],
	},
	cookies: {},
};

const stateItems = {
	navigation: {
		primary: false,
		secondary: false,
	},
	showMenu: false,
	showAllSports: false,
	headerHeight: 0,
	navDrawerOpen: false,
	visibleHeader: false,
	pageHasThirdLevelNav: false,
	oddsFormat: ODDS_FORMAT.AMERICAN,
	usState: '' as StateCode | '',
	ajaxError: false,
	showSpinner: false,
	showAllSportsBottom: false,
	betAdded: false,
	betslipOpen: false,
	showSearch: false,
};
interface IStateActions {
	getCurrentPath: () => string | undefined;

	setHeaderHeight: (height: number) => void;
	setNavDrawerOpen: (bool: boolean) => void;
	setOddsFormat: (format: ODDS_FORMAT) => void;
	setPageHasThirdLevelNav: (bool: boolean) => void;
	setShowAllSports: (bool: boolean) => void;
	setShowAllSportsBottom: (bool: boolean) => void;
	setUsState: (state: StateCode) => void;
	setVisibleHeader: (bool: boolean) => void;
	setNavItem: (currentText: string, newText: string, newUrl: string) => void;
}

const useStore = create<InitialState & typeof stateItems & IStateActions>()(
	devtools((set, get) => ({
		...initialStateData,
		...stateItems,
		// getters
		getCurrentPath: () => get().config.currentPath,

		// setters
		setShowAllSports: bool => set({ showAllSports: bool }),
		setShowAllSportsBottom: bool => set({ showAllSportsBottom: bool }),
		setVisibleHeader: bool => set({ visibleHeader: bool }),
		setNavDrawerOpen: bool => set({ navDrawerOpen: bool }),
		setPageHasThirdLevelNav: bool => set({ pageHasThirdLevelNav: bool }),
		setHeaderHeight: height => set({ headerHeight: height }),
		setOddsFormat: oddsFormat => set({ oddsFormat }),
		setUsState: usState => set({ usState }),
		setNavItem: (currentText: string, newText: string, newUrl: string) => {
			set(state => {
				const item = state.nav.bottomNav.find(
					item => item.text === currentText,
				);
				if (item) {
					item.text = newText;
					item.url = newUrl;
				}
				return { nav: { ...state.nav } };
			});
		},
	})),
);

export const withStore = (BaseComponent: any) => (props: any) =>
	createElement(BaseComponent, {
		...props,
		store: useStore(),
	});

export default useStore;
